import { ArrowLeft, Sparkles, Zap } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { AutomationsBoard } from "@/features/automations/board/AutomationsBoard";
import {
  exploreGroupsForCase,
  starterRunMessage
} from "@/features/automations/board/caseExploreStarters";
import { automationsHint } from "@/features/automations/board/BoardEntry";
import { cn } from "@/lib/utils";

import { useCaseStarters } from "../chat/useCaseStarters";
import { useCaseIde } from "../context/CaseIdeContext";
import { useWorkflowsOptional } from "../workflows/context/WorkflowsContext";
import { useCaseAutomations } from "../workflows/hooks/useCaseAutomations";
import { useOpenWorkflowsTab } from "../workflows/hooks/useOpenWorkflowsTab";
import { useCaseVisa } from "../useCaseVisa";

/** Starters shown under the two entries; the composer lists them all. */
const SHOWN = 4;

const CARD =
  "group flex min-h-32 w-full flex-col items-start gap-3 rounded-2xl p-5 text-left transition-colors duration-100 outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [&_svg]:shrink-0";

/**
 * Shown in the center when no tab is open. Two ways in come first: Run Your
 * Automations, which lists the firm's automations here to run on the case,
 * and Let CaseBlink Prepare, which opens the Case Automations tab. Under them,
 * the agent's starters for the case, the same ones its composer offers; a
 * starter fills the composer to send.
 */
export const EditorEmptyState: FC = () => {
  const { requestPrompt, requestRunAutomation, requestSend, individualId } =
    useCaseIde();
  const { starters } = useCaseStarters(individualId);
  const workflows = useWorkflowsOptional();
  const workflowsTab = useOpenWorkflowsTab();
  const automations = useCaseAutomations();
  const caseVisa = useCaseVisa();
  const exploreGroups = useMemo(
    () => exploreGroupsForCase(caseVisa || null),
    [caseVisa]
  );
  const [listing, setListing] = useState(false);

  const pills = starters.slice(0, SHOWN).map((s) => (
    <Button
      key={s}
      variant="pill"
      size="sm"
      className="px-3"
      onClick={() => requestPrompt(s)}
    >
      {s}
    </Button>
  ));

  if (workflows && listing) {
    // The scroller stays justify-start. my-auto centers a short board;
    // a tall one keeps its top inside the scrollport.
    return (
      <div className="@container flex h-full min-h-0 min-w-0 flex-1 flex-col items-stretch justify-start overflow-y-auto p-8">
        <div
          className="mx-auto my-auto flex w-full max-w-xl shrink-0 flex-col gap-5"
          data-testid="editor-automations"
        >
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 text-fg-2"
              onClick={() => setListing(false)}
              data-testid="editor-automations-back"
            >
              <ArrowLeft />
              Back
            </Button>
            <h2 className="text-md font-medium text-fg">Your Automations</h2>
          </div>
          <AutomationsBoard
            skills={automations}
            exploreGroups={exploreGroups}
            onRun={(skill) => requestRunAutomation(skill.id)}
            onRunStarter={(starter) => requestSend(starterRunMessage(starter))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="@container flex h-full min-h-0 min-w-0 flex-1 flex-col items-stretch justify-start overflow-y-auto p-8 text-center">
      <div className="my-auto flex w-full shrink-0 flex-col items-center gap-8">
        {workflows ? (
          <div
            className="grid w-full max-w-xl gap-3 @lg:grid-cols-2"
            data-testid="editor-entries"
          >
            <button
              type="button"
              onClick={() => setListing(true)}
              data-testid="editor-show-automations"
              className={cn(
                CARD,
                "bg-elevated inset-ring inset-ring-line-2 hover:bg-fill-1"
              )}
            >
              <span className="inline-flex size-10 items-center justify-center rounded-xl bg-fill-2 text-fg-2 [&_svg]:size-5">
                <Zap aria-hidden />
              </span>
              <span className="flex flex-col gap-1">
                <span className="text-md font-medium text-fg">
                  Run Your Automations
                </span>
                <span className="text-base text-fg-3">
                  {automationsHint(automations.length)}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={workflowsTab.openPrepare}
              data-testid="editor-show-workflows"
              data-variant="accent"
              className={cn(
                CARD,
                "button-shimmer-accent bg-accent-soft inset-ring inset-ring-accent-line"
              )}
            >
              <span className="inline-flex size-10 items-center justify-center rounded-xl bg-accent-line text-accent-seed [&_svg]:size-5">
                <Sparkles aria-hidden />
              </span>
              <span className="text-md font-medium text-fg">
                Let CaseBlink Prepare
              </span>
            </button>
          </div>
        ) : null}
        {pills.length ? (
          <div className="flex max-w-lg flex-col items-center gap-3">
            <span className="text-sm text-fg-4">
              {workflows ? "Or Ask Casey" : "Ask Casey"}
            </span>
            <div className="flex flex-wrap justify-center gap-2">{pills}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
